import { Injectable } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Router } from '@angular/router';
import { EMPTY, Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AccountService } from 'src/app/shared/services/account.service';
import { UserStateService } from 'src/app/shared/services/user-state.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  constructor(
    private readonly router: Router,
    private readonly accountService: AccountService,
    private readonly userState: UserStateService
  ) { }

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        const hasAuthenticatedUser = !!this.userState.user?._id;
        const isSessionExpired = this.accountService.shouldTreatUnauthorizedAsSessionExpiry(error);

        /**
         * During a manual logout the server has just invalidated the session,
         * so any in-flight authenticated request will return 401
         * "Authentication failed". Those are expected cleanup failures — swallow
         * them so callers don't toast a backend error message on top of the
         * "Logged out successfully" feedback. Auth endpoints still propagate so
         * legitimate login/signup errors (invalid credentials etc.) surface.
         */
        if (error?.status === 401 && !isSessionExpired && !this.isAuthRoute(req.url)) {
          return EMPTY;
        }

        /**
         * Guests should be allowed to stay on public pages even if a page makes
         * an authenticated request that returns 401/403. Only force a sign-in
         * redirect when an already-authenticated session has actually failed.
         */
        if (isSessionExpired && !this.isAuthRoute(req.url) && hasAuthenticatedUser) {
          this.userState.clearAuthState();
          this.accountService.updateAuthentication(false);
          this.router.navigate(['/sign-in']);
        }

        return throwError(() => error);
      })
    );
  }

  /**
   * The endpoints that own the session itself. Their 401s are meaningful answers rather
   * than incidental failures, so they must reach their caller instead of being swallowed
   * or turned into a redirect.
   *
   * These match `AuthService`'s real URLs, all of which are built from
   * `API_BASE_URL_V3` (`.../api/v3/market-place`). The previous `/user/auth/me` and
   * `/user/regulated-sign-in` were left behind by a URL migration and matched nothing in
   * any environment, so the session probe fell through to the branches below: a 401 on
   * `/auth/me` returned EMPTY, the guard that awaited it emitted no value, and the
   * router silently dropped the navigation.
   */
  private isAuthRoute(url: string): boolean {
    return url.includes('/auth/login')
      || url.includes('/market-place/auth/logout')
      || url.includes('/market-place/auth/me')
      || url.includes('/market-place/regulated-sign-in');
  }
}
